import { When } from 'react-if';
import {
  currencyFormatter,
  ISenderDetail,
  PortalLineItemField,
  toDecimal,
} from 'shared';

import { DatePopover } from '../DatePopover';
import { InvoiceDetails } from '../../lib/invoiceHelper';
import { classNames } from '../../lib/classNames';

const numericColumns = [
  { field: PortalLineItemField.Quantity, label: `Qty`, width: `3.5rem` },
  { field: PortalLineItemField.UnitAmount, label: `Amount`, width: `6.25rem` },
  { field: PortalLineItemField.Total, label: `Total`, width: `6.875rem` },
];
const padX = `px-[clamp(1rem,5cqi,2.5rem)]`;

function TotalRow({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={ classNames(`flex justify-between gap-4 px-3.5 py-[0.4375rem]`, className || `text-gray-600`) }>
      <span>{ label }</span>
      <span>{ value }</span>
    </div>
  );
}

interface InvoicePaperProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invoice: any;
  details: InvoiceDetails;
  sender: ISenderDetail;
  /** Under the "Bill to" label */
  billTo: React.ReactNode;
  /** Extra rows at the end of the invoice meta list, as dt/dd pairs */
  meta?: React.ReactNode;
  /** Straps and the payment plan strip, above the document */
  top?: React.ReactNode;
  /** Pinned to the bottom of the card */
  bottom?: React.ReactNode;
  mode: `app` | `portal`;
  /** Portal only, the org's hidden line item columns. Their values are already stripped by the API */
  hiddenLineItemFields?: PortalLineItemField[];
}

/** The invoice laid out as a paper document (design 6b) */
export function InvoicePaper({ invoice, details, sender, billTo, meta, top, bottom, mode, hiddenLineItemFields = [] }: InvoicePaperProps) {
  const formatter = currencyFormatter(invoice.xero_currency);
  const timezone = details.organisationTimezone;
  const shouldRenderDueCurrency = !/[a-z]/i.test(details.dueAmount ?? ``);
  const columns = numericColumns.filter(column => !hiddenLineItemFields.includes(column.field));
  const lineGridStyle = { gridTemplateColumns: [`minmax(0,1fr)`, ...columns.map(column => column.width)].join(` `) };
  const showDescriptionHeader = !hiddenLineItemFields.includes(PortalLineItemField.Description) || !hiddenLineItemFields.includes(PortalLineItemField.Tracking);
  const showTotal = !hiddenLineItemFields.includes(PortalLineItemField.Total);

  return (
    <div className={ `@container flex min-h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white` }>
      { top }

      { /* Sender and invoice meta */ }
      <div className={ classNames(
        `flex flex-wrap justify-between gap-x-8 gap-y-5`,
        padX,
        mode === `portal` ? `pt-[clamp(1.25rem,5cqi,2.5rem)]` : `pt-8`,
      ) }>
        <div className={ `flex min-w-0 flex-col gap-1.5` }>
          <span className={ `text-lg font-semibold text-gray-900` }>{ sender.customerFacingCompanyName }</span>
          <span className={ `text-[0.8125rem] leading-relaxed text-gray-500` }>
            <When condition={ !!sender.companyNumber }>
              { `${sender.companyNumberType || ``} ${sender.companyNumber}` }
              <br />
            </When>
            { sender.taxNumber }
          </span>
        </div>
        <dl className={ `grid grid-cols-[auto_auto] content-start gap-x-6 gap-y-1.5 text-[0.8125rem]` }>
          <dt className={ `text-gray-500` }>{ `Invoice` }</dt>
          <dd className={ `text-right font-semibold text-gray-900` }>{ details.number ? `#${details.number}` : `-` }</dd>
          <dt className={ `text-gray-500` }>{ `Issued` }</dt>
          <dd className={ `text-right text-gray-900` }>
            <DatePopover
              disablePopover
              date={ details.issueDateString }
              labelFormat={ `d LLL yyyy` }
              organisationTimezone={ timezone }
            />
          </dd>
          <dt className={ `text-gray-500` }>{ `Due` }</dt>
          <dd className={ `text-right text-gray-900` }>
            <DatePopover
              disablePopover
              date={ details.dueDateString }
              labelFormat={ `d LLL yyyy` }
              organisationTimezone={ timezone }
              time={ `end` }
            />
          </dd>
          <When condition={ !!details.reference && details.reference.length < 50 }>
            <dt className={ `text-gray-500` }>{ `Reference` }</dt>
            <dd className={ `text-right text-gray-900` }>{ details.reference }</dd>
          </When>
          { meta }
        </dl>
      </div>

      { /* Bill to */ }
      <div className={ classNames(`flex flex-col gap-1.5 pt-6`, padX) }>
        <span className={ `text-xs font-medium uppercase tracking-[0.04em] text-gray-500` }>{ `Bill to` }</span>
        { billTo }
      </div>

      { /* Line items, stacked when the card is narrow. Empty when the org hides them all */ }
      <When condition={ details.lineItems.length > 0 }>
        <div className={ classNames(`pt-7 text-sm`, padX) }>
          <div
            className={ `hidden gap-5 rounded-lg bg-paidniceGreen-50 px-3.5 py-2.5 text-[0.8125rem] font-semibold text-paidniceGreen-800 @lg:grid` }
            style={ lineGridStyle }
          >
            <span>{ showDescriptionHeader ? `Description` : `` }</span>
            { columns.map(column => (
              <span
                key={ column.field }
                className={ `text-right` }
              >
                { column.label }
              </span>
            )) }
          </div>
          <div className={ `flex justify-between rounded-lg bg-paidniceGreen-50 px-3.5 py-2.5 text-[0.8125rem] font-semibold text-paidniceGreen-800 @lg:hidden` }>
            <span>{ showDescriptionHeader ? `Description` : `` }</span>
            <span>{ showTotal ? `Total` : `` }</span>
          </div>
          { details.lineItems.map((item, index) => {
            const description = (
              <div className={ `min-w-0` }>
                <div className={ `whitespace-pre-line break-words leading-relaxed` }>{ item.description }</div>
                { item.tracking ? (
                  <div className={ `text-xs italic text-gray-400` }>{ `Tracking: ${item.tracking}` }</div>
                ) : null }
              </div>
            );
            const totalTitle = item.taxAmount ? `tax: ${item.taxAmount}` : `not taxed`;
            const values: Record<string, React.ReactNode> = {
              [PortalLineItemField.Quantity]: item.quantity,
              [PortalLineItemField.UnitAmount]: item.amount,
              [PortalLineItemField.Total]: item.total,
            };
            const quantityTimesAmount = [item.quantity, item.amount].filter(value => value !== undefined && value !== ``).join(` × `);

            return (
              <div
                key={ `lineitem-${index}-${item.description}` }
                className={ `border-b border-gray-100 px-3.5 tabular-nums text-gray-700` }
              >
                <div
                  className={ `hidden gap-5 py-[0.6875rem] @lg:grid` }
                  style={ lineGridStyle }
                >
                  { description }
                  { columns.map(column => (
                    <span
                      key={ column.field }
                      className={ classNames(`text-right`, column.field === PortalLineItemField.Total && `text-gray-900`) }
                      title={ column.field === PortalLineItemField.Total ? totalTitle : undefined }
                    >
                      { values[column.field] }
                    </span>
                  )) }
                </div>
                <div className={ `flex flex-col gap-1 py-3 @lg:hidden` }>
                  { description }
                  <span className={ `flex justify-between text-[0.8125rem] text-gray-500` }>
                    <span>{ quantityTimesAmount }</span>
                    <span
                      className={ `text-sm text-gray-900` }
                      title={ totalTitle }
                    >
                      { item.total }
                    </span>
                  </span>
                </div>
              </div>
            );
          }) }
        </div>
      </When>

      { /* Totals */ }
      <div className={ classNames(`flex justify-end pt-4`, padX, mode === `portal` ? `pb-[clamp(1.25rem,5cqi,2rem)]` : `pb-8`) }>
        <div className={ `flex w-[18.75rem] max-w-full flex-col text-sm tabular-nums` }>
          <TotalRow
            label={ `Subtotal` }
            value={ details.subTotal }
          />
          <TotalRow
            label={ `Tax` }
            value={ details.tax }
          />
          <When condition={ invoice.total_discount_cents > 0 }>
            <TotalRow
              label={ `Discounts` }
              value={ details.discounts }
            />
          </When>
          <TotalRow
            label={ `Total` }
            value={ details.total }
            className={ `font-semibold text-gray-900` }
          />
          <TotalRow
            label={ `Amount paid` }
            value={ `${invoice.total_paid_cents > 0 ? `- ` : ``}${details.paidAmount}` }
          />
          <When condition={ !!details.depositAmount }>
            <TotalRow
              label={ `Includes deposit` }
              value={ details.depositAmount }
              className={ `-mt-1 text-xs text-gray-500` }
            />
          </When>
          <When condition={ !!details.dateFullyPaid }>
            <TotalRow
              label={ `Paid on` }
              value={ (
                <DatePopover
                  date={ details.dateFullyPaid }
                  organisationTimezone={ timezone }
                  labelFormat={ `d LLL yyyy` }
                  disablePopover
                />
              ) }
            />
          </When>
          { (details.creditNotes || []).map(creditNote => (
            <TotalRow
              key={ creditNote.creditNoteExternalId }
              label={ `Credit note ${creditNote.creditNoteNumber}` }
              value={ `- ${formatter(toDecimal(creditNote.appliedAmountInCents))}` }
            />
          )) }
          <div className={ `mt-2 flex items-baseline justify-between gap-4 rounded-lg bg-paidniceGreen-600 px-3.5 py-3 text-white` }>
            <span className={ `font-semibold` }>{ `Total due` }</span>
            <span className={ `text-lg font-bold` }>
              { details.dueAmount }
              { shouldRenderDueCurrency ? ` ${details.currency}` : `` }
            </span>
          </div>
        </div>
      </div>

      <When condition={ !!details.reference && details.reference.length >= 50 }>
        <div className={ classNames(`whitespace-pre-line pb-6 text-sm text-gray-600`, padX) }>
          <span className={ `font-medium text-gray-900` }>{ `Invoice memo: ` }</span>
          { details.reference }
        </div>
      </When>

      { /* Pinned to the bottom of the card when the invoice is shorter than the column */ }
      <div className={ `mt-auto` }>
        { bottom }
      </div>
    </div>
  );
}
